/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import "../assets/css/bootstrap/bootstrap.css";
import "../assets/css/bootstrap/bootstrap-grid.css";
import "../assets/css/bootstrap/bootstrap-reboot.css";
import "../assets/css/backend-plugin.min.css";
import "../assets/vendor/remixicon/fonts/remixicon.css";
import "../assets/vendor/line-awesome/dist/line-awesome/css/line-awesome.min.css";
import "../assets/vendor/@fortawesome/fontawesome-free/css/all.min.css";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const getPasswordError = (password: string) => {
  if (password.length < 8) {
    return "Le mot de passe doit contenir au moins 8 caracteres.";
  }
  if (
    !/[a-z]/.test(password) ||
    !/[A-Z]/.test(password) ||
    !/[0-9]/.test(password) ||
    !/[^A-Za-z0-9]/.test(password)
  ) {
    return "Le mot de passe doit contenir une majuscule, une minuscule, un chiffre et un caractere special.";
  }
  return "";
};

const ForgotPasswordCover: React.FC = () => {
  const { translate } = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get("token") || "");
  const isResetStep = Boolean(token);

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(isResetStep);
  const [tokenValid, setTokenValid] = useState(!isResetStep);
  const [alertMessage, setAlertMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const submitBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && submitBtnRef.current) submitBtnRef.current.click();
    };
    document.body.addEventListener("keypress", handleKeyPress);
    return () => document.body.removeEventListener("keypress", handleKeyPress);
  }, []);

  useEffect(() => {
    if (token && window.location.search.includes("token=")) {
      navigate("/forgot-password", { replace: true });
    }
  }, [navigate, token]);

  useEffect(() => {
    if (!token) {
      setValidatingToken(false);
      setTokenValid(true);
      return;
    }

    let mounted = true;
    setValidatingToken(true);

    axios
      .get(`${backendUrl}/api/forgot-password/validate`, {
        params: { token },
      })
      .then(() => {
        if (!mounted) return;
        setTokenValid(true);
        setAlertMessage("");
      })
      .catch((error) => {
        if (!mounted) return;
        setTokenValid(false);
        setSuccess(false);
        setAlertMessage(
          error?.response?.data?.message || "Lien invalide ou expire.",
        );
      })
      .finally(() => {
        if (mounted) setValidatingToken(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const requestResetLink = async () => {
    if (!email.trim()) {
      setSuccess(false);
      setAlertMessage("Veuillez saisir votre email.");
      return;
    }

    try {
      setLoading(true);
      setAlertMessage("");
      setSuccess(false);

      const { data } = await axios.post(`${backendUrl}/api/forgot-password`, {
        email: email.trim(),
      });

      setSuccess(true);
      setAlertMessage(
        data?.message ||
          "Email valide. Un lien securise pour changer votre mot de passe a ete envoye.",
      );
    } catch (error: any) {
      setSuccess(false);
      setAlertMessage(
        error?.response?.data?.message ||
          translate("connectionProblem") ||
          "Connection error.",
      );
      console.error("Forgot password email request error", error);
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setSuccess(false);
      setAlertMessage("Veuillez remplir tous les champs.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setSuccess(false);
      setAlertMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setSuccess(false);
      setAlertMessage(passwordError);
      return;
    }

    try {
      setLoading(true);
      setAlertMessage("");
      setSuccess(false);

      const { data } = await axios.post(`${backendUrl}/api/forgot-password/reset`, {
        token,
        newPassword,
        confirmPassword,
      });

      setSuccess(true);
      setAlertMessage(data?.message || "Mot de passe modifie avec succes.");

      window.setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1400);
    } catch (error: any) {
      setSuccess(false);
      setAlertMessage(
        error?.response?.data?.message ||
          translate("connectionProblem") ||
          "Connection error.",
      );
      console.error("Forgot password reset error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isResetStep) {
      submitNewPassword();
      return;
    }

    requestResetLink();
  };

  return (
    <div className="wrapper">
      <section className="login-content">
        <div className="row">
          <div className="col-sm-7" style={{ margin: 0, padding: 0 }}>
            <div className="aida-bg-auth"></div>
          </div>

          <div className="col-sm-5" style={{ padding: "25px 30px" }}>
            <div className="card-body p-0">
              <div className="row d-flex flex-column align-items-center justify-content-center">
                <div className="col-lg-12 text-center">
                  <img
                    src="asset/images/logo.png"
                    alt="AIDA"
                    className="img-fluid small-logo"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      marginTop: "40px",
                    }}
                  />
                </div>
              </div>

              <div
                className="d-flex align-items-center auth-bg px-2 p-lg-5"
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  height: "75vh",
                  paddingTop: "15vh",
                  transform: "translateY(-15%)",
                }}
              >
                <div className="col-12 col-sm-8 col-md-6 col-lg-12 px-xl-2 mx-auto">
                  <h2 className="card-title mb-1">
                    {isResetStep ? "Nouveau mot de passe" : "Mot de passe oublie"}
                  </h2>

                  <p className="card-text mb-2">
                    {isResetStep
                      ? "Choisissez un nouveau mot de passe pour votre espace AIDA."
                      : "Saisissez votre email. S'il est valide, vous recevrez un lien securise pour changer votre mot de passe."}
                  </p>

                  <form
                    className="auth-forgot-password-form mt-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!validatingToken && tokenValid) handleSubmit();
                    }}
                    noValidate
                  >
                    {!isResetStep && (
                      <div className="mb-3">
                        <label className="form-label" htmlFor="forgot-password-email">
                          {translate("Email")}
                        </label>
                        <input
                          className="form-control"
                          id="forgot-password-email"
                          type="email"
                          placeholder="john@example.com"
                          autoFocus
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    )}

                    {isResetStep && tokenValid && !validatingToken && (
                      <>
                        <div className="mb-3">
                          <label className="form-label" htmlFor="new-password">
                            Nouveau mot de passe
                          </label>
                          <input
                            className="form-control"
                            id="new-password"
                            type="password"
                            placeholder="Majuscule, minuscule, chiffre et caractere special"
                            autoFocus
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={loading}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label" htmlFor="confirm-password">
                            Confirmation du mot de passe
                          </label>
                          <input
                            className="form-control"
                            id="confirm-password"
                            type="password"
                            placeholder="Confirmez le nouveau mot de passe"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                          />
                        </div>
                      </>
                    )}

                    {validatingToken && (
                      <div className="text-center my-4">
                        <BeatLoader loading={validatingToken} size={10} />
                      </div>
                    )}

                    <button
                      ref={submitBtnRef}
                      type="submit"
                      className="btn btn-primary w-100 mb-1 mt-3"
                      disabled={loading || validatingToken || !tokenValid}
                    >
                      {isResetStep ? "Modifier le mot de passe" : "Envoyer le lien"}
                    </button>

                    {loading && (
                      <div className="text-center mt-2">
                        <BeatLoader loading={loading} size={10} />
                      </div>
                    )}
                  </form>

                  {alertMessage && (
                    <div
                      style={{
                        padding: "12px",
                        marginTop: "12px",
                        border: "1px solid transparent",
                        borderRadius: "4px",
                        color: success ? "#0f5132" : "#842029",
                        backgroundColor: success ? "#d1e7dd" : "#f8d7da",
                        borderColor: success ? "#badbcc" : "#f5c2c7",
                        textAlign: "center",
                      }}
                      role="alert"
                    >
                      {alertMessage}
                    </div>
                  )}

                  <p className="text-center mt-2">
                    <Link to="/login" style={{ textDecoration: "none" }}>
                      <span style={{ marginRight: 6 }}>{"<"}</span>
                      {translate("Back to login")}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPasswordCover;
